import { AlertCircle, type LucideIcon, Pencil } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { Alert, AlertTitle } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'

/**
 * Editable field primitives.
 *
 * Four pieces that compose:
 *
 * - `EditableField` - pure layout. Label + optional icon on the left,
 *   an `action` slot on the right, content below, optional error
 *   surface at the bottom. Doesn't know anything about edit modes.
 * - `EditButton` - the pencil that enters edit mode. Lives in the
 *   `action` slot of `EditableField` and hides itself once editing
 *   starts, since `EditActions` takes over from there.
 * - `EditActions` - the Cancel / Save pair that closes the edit out.
 *   Renders below the edit surface (last child of `EditableField`)
 *   and is `null` outside edit mode, so callers can drop it in
 *   unconditionally.
 * - `useEditableField<T>` - the inline-edit state pattern:
 *   `isEditing`, `draft`, `error`, `isSaving`, plus `startEditing` /
 *   `cancelEditing` / `save` callbacks.
 *
 * The split lets callers reach for only what they need:
 * - Full inline edit: all four.
 * - Nav-away edit (email, phone): `EditableField` + a `<Link>` in the
 *   `action` slot, no hook.
 * - Read-only fact: `EditableField` alone, no `action`.
 */

// ---------------------------------------------------------------------------
// EditableField (layout)
// ---------------------------------------------------------------------------

export function EditableField({
  label,
  icon: Icon,
  action,
  error,
  children,
}: {
  label: string
  /** Optional leading icon. Lucide only, sized to sit next to the label. */
  icon?: LucideIcon
  /**
   * Right-aligned action slot in the header row. Typically an
   * `<EditButton>` for inline-edit, a nav `<Link>` for
   * editHref-style fields, or omitted for read-only rows.
   */
  action?: ReactNode
  /**
   * Optional inline error content. Rendered in a consistent
   * destructive-tinted surface below the field body. Callers own the
   * error string / node - this component is a passthrough.
   */
  error?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2 border-border border-b py-4 last:border-b-0">
      <div className="flex min-h-7 items-center justify-between gap-4">
        <p className="flex items-center gap-1.5 text-button-sm text-muted-foreground">
          {Icon ? <Icon className="size-3.5" aria-hidden="true" /> : null}
          {label}
        </p>
        {action}
      </div>

      {error ? (
        <Alert variant="destructive" className="mb-1">
          <AlertCircle />
          <AlertTitle>{error}</AlertTitle>
        </Alert>
      ) : null}

      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// EditableValue (shared read-mode value styling)
// ---------------------------------------------------------------------------

/**
 * Standard read-mode value styling for use inside an `EditableField`.
 * Kept as a peer component (rather than baked into `EditableField`)
 * so callers can wrap arbitrary nodes without needing the field
 * itself to be state-aware.
 */
export function EditableValue({ children }: { children: ReactNode }) {
  return <div className="text-label-md text-[15px] text-neutral-600">{children}</div>
}

// ---------------------------------------------------------------------------
// EditableSubfield (labelled row for multi-input editable fields)
// ---------------------------------------------------------------------------

/**
 * Labelled wrapper for individual inputs inside a multi-input `EditableField`.
 */
export function EditableSubfield({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="px-2 text-xs text-neutral-500/80 font-semibold translate-y-0.5">
        {label}
      </span>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// EditButton (pencil that enters edit mode)
// ---------------------------------------------------------------------------

/**
 * Pencil affordance for the `action` slot of `EditableField`. Renders
 * nothing while the field is being edited - `EditActions` owns the
 * exits from that point, so leaving the pencil up would offer a third,
 * redundant control. Wire the whole `useEditableField` return value
 * through the `field` prop.
 *
 * `label` supplies the human-readable field name for the pencil's
 * `aria-label`, e.g. `label="name"` yields `Edit name`.
 */
export function EditButton<T>({ field, label }: { field: EditableFieldState<T>; label: string }) {
  if (field.isEditing) return null

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      onClick={field.startEditing}
      aria-label={`Edit ${label}`}
      className="text-muted-foreground hover:text-foreground"
    >
      {/* Explicitly sized down - Lucide's Pencil fills more of its
          bounding box than most icons, so at `size-4` it reads
          visually larger than its neighbours. */}
      <Pencil className="size-3.5" />
    </Button>
  )
}

// ---------------------------------------------------------------------------
// EditActions (cancel / save pair below the edit surface)
// ---------------------------------------------------------------------------

/**
 * Cancel / Save pair for the bottom of an inline-edit surface. Render
 * it as the last child of `EditableField`; it's `null` outside edit
 * mode, so it can sit alongside the caller's `isEditing` ternary
 * rather than inside it.
 */
export function EditActions<T>({ field }: { field: EditableFieldState<T> }) {
  if (!field.isEditing) return null

  return (
    <div className="mt-1 flex justify-end gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={field.cancelEditing}
        disabled={field.isSaving}
      >
        Cancel
      </Button>
      <Button type="button" size="sm" onClick={field.save} disabled={field.isSaving}>
        {field.isSaving ? 'Saving…' : 'Save'}
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// useEditableField (state hook)
// ---------------------------------------------------------------------------

export interface EditableFieldState<T> {
  isEditing: boolean
  isSaving: boolean
  draft: T
  setDraft: (next: T) => void
  /**
   * Last save error as a plain string, or `null` when clear.
   * Callers set this explicitly inside `onSave` via the injected
   * `setError`; the hook never auto-captures thrown exceptions.
   */
  error: string | null
  setError: (next: string | null) => void
  /** Enter edit mode; reseeds `draft` from `initialValue` and clears any error. */
  startEditing: () => void
  /** Leave edit mode without saving; discards the draft, clears any error. */
  cancelEditing: () => void
  /**
   * Run the caller's `onSave` with the current draft. Closes edit
   * mode if the caller doesn't set an error; keeps it open if they
   * do. Unhandled throws from `onSave` are logged and left to
   * propagate so bugs surface via error boundaries / Sentry rather
   * than getting rendered as user-facing copy.
   */
  save: () => Promise<void>
}

/**
 * Context object passed to `onSave`. Callers explicitly signal
 * failure by calling `setError` (edit mode stays open with the
 * message shown); returning without setting an error closes edit
 * mode. This keeps error UI explicit - a thrown exception from a
 * bug in the caller's save logic is never displayed to the user as
 * a message.
 */
export interface EditableFieldSaveContext<T> {
  value: T
  setError: (message: string | null) => void
}

export interface UseEditableFieldParams<T> {
  initialValue: T
  onSave: (ctx: EditableFieldSaveContext<T>) => Promise<void>
}

/**
 * Local state for an inline-edit field. `initialValue` seeds the
 * draft on `startEditing` and on `cancelEditing`; the underlying
 * prop can change freely while the field is idle (a refetch after
 * a successful save flows in seamlessly - the next `startEditing`
 * reseeds from the newer value).
 *
 * `onSave` receives `{ value, setError }`. Call `setError(msg)` to
 * show a message and keep edit mode open; return without calling it
 * to close on success. Unexpected throws are re-raised (logged to
 * the console first) - the hook deliberately doesn't swallow them
 * as UI copy, so runtime bugs stay visible to observability tools.
 */
export function useEditableField<T>({
  initialValue,
  onSave,
}: UseEditableFieldParams<T>): EditableFieldState<T> {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState<T>(initialValue)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startEditing = () => {
    setDraft(initialValue)
    setError(null)
    setIsEditing(true)
  }
  const cancelEditing = () => {
    setDraft(initialValue)
    setError(null)
    setIsEditing(false)
  }
  const save = async () => {
    setIsSaving(true)
    // Track whether the caller flagged a failure via `setError`. If
    // they did, keep edit mode open; otherwise close on success.
    // Wrapping the setter keeps callers on a plain `setError(msg)`
    // API - React state closures mean we can't read `error` after
    // the await, so we watch the setter call itself.
    let shouldClose = true
    const trackedSetError = (msg: string | null) => {
      setError(msg)
      if (msg !== null) shouldClose = false
    }
    try {
      await onSave({ value: draft, setError: trackedSetError })
      if (shouldClose) {
        setError(null)
        setIsEditing(false)
      }
    } finally {
      setIsSaving(false)
    }
  }

  return {
    isEditing,
    isSaving,
    draft,
    setDraft,
    error,
    setError,
    startEditing,
    cancelEditing,
    save,
  }
}
